import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives";

import ContentPlatformImage from "@/components/content-platform-image/src";
import { Author } from "@/content/templates/template.types";
import { expectContent } from "@/content/utils/expectContent";

export interface AgileHeadingProps {
    title: string;
    eyebrow?: string | null;
    description?: string | null;
    imageUrl?: string | null;
    author?: Author | null;
}

const headerStyles = css({
    backgroundSize: "cover",
    paddingBlock: "2rem 0",
    display: "grid",
    placeItems: "center"
});
const eyebrowStyles = css({
    color: "#42526e",
    fontSize: "0.75rem",
    letterSpacing: "1px",
    lineHeight: "2",
    textTransform: "uppercase"
});

const h1Styles = css({
    fontWeight: "500",
    fontSize: "2.3rem",
    marginBlockStart: "16px",
    marginBlockEnd: "16px",
    letterSpacing: "0",
    lineHeight: "1.25",
    color: "#253858",

    [media.above.lg]: {
        marginBlockEnd: "24px"
    }
});

const pStyles = css({
    fontSize: "1.25rem",
    lineHeight: "1.4",
    marginBlockEnd: "16px",
    color: "#253858",
    [media.above.md]: {
        marginBlockEnd: "24px"
    }
});

// TODO: Switch to shared Article Grid component
// or at least exported widths/paddings, once in place
const contentBlockStyles = css({
    width: "100%",
    maxWidth: "1272px",
    padding: "1rem 2rem 0 2rem",
    background: "#ffffff",

    [media.above.sm]: {
        paddingInline: "60px"
    }
});
const authorStyles = css({
    fontSize: ".875rem",
    display: "flex",
    gap: "1rem",
    alignItems: "center",
    textTransform: "uppercase"
});
const headshotStyles = css({
    borderRadius: "50%",
    width: "2.25rem",
    height: "2.25rem",
    objectFit: "cover"
});

export const AgileHeading = ({
    title,
    eyebrow,
    description,
    imageUrl,
    author
}: AgileHeadingProps) => {
    return (
        <header
            css={headerStyles}
            style={
                imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined
            }
        >
            <div css={contentBlockStyles}>
                {eyebrow && (
                    <p className="eyebrow-sm" css={eyebrowStyles}>
                        {eyebrow}
                    </p>
                )}
                <h1 css={h1Styles}>{title}</h1>
                {description && (
                    <p css={pStyles} className="body-lg">
                        {description}
                    </p>
                )}
                {author && (
                    <p className="body-sm" css={authorStyles}>
                        {author.authorPicture?.url && (
                            <ContentPlatformImage
                                src={author.authorPicture.url}
                                baseWidth={36}
                                width={36}
                                height={36}
                                alt=""
                                css={headshotStyles}
                            />
                        )}
                        <FormattedMessage
                            id="agile.heading.author.byline"
                            defaultMessage="by {authorName}"
                            values={{
                                authorName: expectContent(
                                    author.name,
                                    "Author name not found"
                                )
                            }}
                        />
                    </p>
                )}
            </div>
        </header>
    );
};
